import React, { useState, useEffect } from "react";
import axios from "axios";

const CourseMaterialPage = () => {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courseMaterials, setCourseMaterials] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [currentMaterialId, setCurrentMaterialId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // SESSION
  const sessionId = localStorage.getItem("currentSessionId");

  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-[14px] focus:ring-2 focus:ring-blue-500 placeholder:text-[14px] placeholder:text-gray-400";
  const tableTdStyle = "border border-gray-300 px-4 py-2 text-center text-[14px]";

  // ---------------------- FETCH CLASSES ----------------------
  useEffect(() => {
    if (!sessionId) return;

   
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionId}`)
      .then((response) => {
        setClasses(response.data);
      })
      .catch(() => alert("Error fetching classes"));
  }, [sessionId]);
  // ---------------------- FETCH SECTIONS & SUBJECTS ----------------------
  useEffect(() => {
    if (!selectedClass || !sessionId) return;

    const encodedClass = encodeURIComponent(selectedClass);

    Promise.all([
      axios.get(
  `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`,
  { params: { sessionId } }
),
      // Subjects
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${encodeURIComponent(selectedClass)}`, {
        params: { sessionId }
      })
    ])
      .then(([sectionRes, subjectRes]) => {
        setSections(sectionRes.data);
        setSubjects(subjectRes.data);
      })
      .catch(() => alert("Error fetching sections or subjects"));
  }, [selectedClass, sessionId]);

  // ---------------------- FETCH COURSE MATERIALS ----------------------
  const fetchCourseMaterials = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/coursematerial/getAllCourseMaterials`,
        { params: { sessionId } }
      );
      setCourseMaterials(res.data);
    } catch (err) {
      alert("Error fetching course materials");
    }
  };

  useEffect(() => {
    fetchCourseMaterials();
  }, [sessionId]);

  // ---------------------- SUBMIT / UPDATE ----------------------
  const handleSubmit = async () => {
    if (
      !selectedClass ||
      !selectedSection ||
      !selectedSubject ||
      !title ||
      (!file && !isUpdating)
    ) {
      alert("Please fill all required fields.");
      return;
    }

    if (isSubmitting) return; // prevent double submit
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("className", selectedClass);
    formData.append("sectionName", selectedSection);
    formData.append("subjectName", selectedSubject);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("sessionId", sessionId);
    if (file) formData.append("file", file);

    try {
      if (isUpdating && currentMaterialId) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/coursematerial/updateCourseMaterial/${currentMaterialId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        alert("Course material updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/coursematerial/createCourseMaterial`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        alert("Course material uploaded successfully!");
      }

      clearForm();
      fetchCourseMaterials();
    } catch {
      alert("Error uploading or updating course material");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------- DELETE ----------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this material?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/coursematerial/deleteCourseMaterial/${id}`,
        { params: { sessionId } }
      );
      alert("Course material deleted successfully!");
      fetchCourseMaterials();
    } catch {
      alert("Error deleting material");
    }
  };

  // ---------------------- UPDATE FORM ----------------------
  const handleUpdate = (mat) => {
    setSelectedClass(mat.className);
    setSelectedSection(mat.sectionName);
    setSelectedSubject(mat.subjectName);
    setTitle(mat.title);
    setDescription(mat.description || "");
    setIsUpdating(true);
    setCurrentMaterialId(mat._id);
  };

  const clearForm = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setTitle("");
    setDescription("");
    setFile(null);
    setIsUpdating(false);
    setCurrentMaterialId(null);
  };

  return (
    <div className="container mx-auto p-4">
      {/* CLASS SELECT */}
      <div className="mb-4">
        <label className={labelStyle}>Class Name:</label>
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          className={inputStyle}
        >
          <option value="">Select Class</option>
          {classes.map((cls) => (
            <option key={cls._id} value={cls.className}>
              {cls.className}
            </option>
          ))}
        </select>
      </div>

      {/* SECTION SELECT */}
      {selectedClass && (
        <div className="mb-4">
          <label className={labelStyle}>Section Name:</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className={inputStyle}
          >
            <option value="">Select Section</option>
            {sections.map((section) => (
              <option key={section._id} value={section.sectionName}>
                {section.sectionName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* SUBJECT SELECT */}
      {selectedClass && selectedSection && (
        <div className="mb-4">
          <label className={labelStyle}>Subject Name:</label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className={inputStyle}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject._id} value={subject.subjectName}>
                {subject.subjectName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Title, File, Description */}
      <div className="flex flex-col md:flex-row md:space-x-4 mb-4">
        <div className="flex-1 mb-4 md:mb-0">
          <label className={labelStyle}>Title:</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputStyle}
            placeholder="Enter title"
          />
        </div>

        <div className="flex-1 mb-4 md:mb-0">
          <label className={labelStyle}>Upload File:</label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className={`${inputStyle} pt-1`}
          />
        </div>

        <div className="flex-1">
          <label className={labelStyle}>Description (Optional):</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputStyle}
            placeholder="Enter description"
          />
        </div>
      </div>

      {/* SUBMIT BUTTON */}
      <button
        onClick={handleSubmit}
        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-[14px]"
        disabled={isSubmitting}
      >
        {isSubmitting ? (isUpdating ? "Updating..." : "Uploading...") : isUpdating ? "Update" : "Submit"}
      </button>

      {isUpdating && (
        <button
          onClick={clearForm}
          className="bg-gray-500 hover:bg-gray-400 text-white py-2 px-4 rounded ml-2 text-[14px]"
        >
          Cancel
        </button>
      )}

      {/* MATERIAL LIST */}
      <h2 className="text-[18px] font-semibold text-black mt-10 mb-4">
        Course Materials
      </h2>

      <table className="table-auto w-full border-collapse border border-gray-300">
        <thead className="bg-gray-200">
          <tr>
            <th className="border px-4 py-2 text-[18px]">Sr No.</th>
            <th className="border px-4 py-2 text-[18px]">Class</th>
            <th className="border px-4 py-2 text-[18px]">Subject</th>
            <th className="border px-4 py-2 text-[18px]">Title</th>
            <th className="border px-4 py-2 text-[18px]">File</th>
            <th className="border px-4 py-2 text-[18px]">Actions</th>
          </tr>
        </thead>

        <tbody className="bg-gray-50">
          {courseMaterials.map((material, index) => (
            <tr key={material._id} className="hover:bg-gray-100">
              <td className={tableTdStyle}>{index + 1}</td>
              <td className={tableTdStyle}>
                {material.className} ({material.sectionName})
              </td>
              <td className={tableTdStyle}>{material.subjectName}</td>
              <td className={tableTdStyle}>{material.title}</td>
              <td className={tableTdStyle}>
                <a
                  href={material.courseMaterialFile}
                  className="text-blue-600 underline text-[14px]"
                  target="_blank"
                  rel="noreferrer"
                >
                  View File
                </a>
              </td>
              <td className={tableTdStyle}>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => handleUpdate(material)}
                    className="bg-yellow-500 hover:bg-yellow-400 text-white px-2 py-1 rounded text-[14px]"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(material._id)}
                    className="bg-red-600 hover:bg-red-500 text-white px-2 py-1 rounded text-[14px]"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CourseMaterialPage;
